/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React, { ReactElement } from 'react';
import { observer } from 'mobx-react';
import { Box, Dialog, Grid, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { Entry } from '../../../../../types/course/Entry';
import EntryDetailViewStyles from './EntryDetailViewStyles';
import { useViewModel } from '../../../../../hooks/useViewModel';
import { EntryDetailViewModel } from './EntryDetailViewModel';
import { ThumbDown } from '@material-ui/icons';
import { EntryContentView } from './EntryContent/EntryContentView';

interface Props {
	open: boolean;
	entry: Entry;
}

export const EntryDetailView = observer((props: Props): ReactElement => {
	const { open, entry } = props;
	const classes = EntryDetailViewStyles();
	const viewModel = useViewModel(EntryDetailViewModel);

	const daysUntilRemoval = viewModel.getDaysUntilRemoval(entry.rejectedDate);

	return (
		<>
			<Dialog
				open={open}
				fullWidth={true}
				classes={{ paper: classes.paper }}
				onClose={() => {
					viewModel.closeDetailView();
				}}>
				<Grid container className={classes.contentContainer}>
					<Grid item xs={6} className={classes.contentLeft}>
						<Box className={classes.imageBox}>
							{entry.state === 'rejected' && (
								<Box className={classes.rejectedBox}>
									<Box className={classes.rejectedTextBox}>
										<ThumbDown className={classes.rejectedIcon} />
										<Typography className={classes.rejectedTitle} variant={'h1'}>
											<FormattedMessage id={'entries.rejected'} />
										</Typography>
									</Box>
									<Box className={classes.rejectedTextBox}>
										<Typography className={classes.rejectedText}>
											({daysUntilRemoval}
											<FormattedMessage id={'entries.days_until_removal'} />)
										</Typography>
									</Box>
								</Box>
							)}
							<img
								src={entry.imageUrl}
								alt={'Entry image'}
								className={classes.image}
								onClick={() => viewModel.setShowDetailImage(true)}
							/>
							<img src={entry.imageUrl} alt={'Blurred entry image'} className={classes.imageBlur} />
						</Box>
					</Grid>
					<Grid item xs={6} className={classes.contentRight}>
						<EntryContentView entry={entry} />
					</Grid>
				</Grid>
			</Dialog>
			<Dialog open={viewModel.getShowDetailImage()} onClose={() => viewModel.setShowDetailImage(false)} maxWidth='lg'>
				<img src={entry.imageUrl} alt='Entry Image' className={classes.detailImage} />
			</Dialog>
		</>
	);
});
